import { createReducer, AnyAction } from '@reduxjs/toolkit';
import { enableMapSet } from 'immer';
enableMapSet();
import * as commonEnums from '../../../enums/index';
import * as cmxDateTime from '../../../core/date-time/cmx-date-time';
import * as documentUtils from '../../../core/document-utils';
import * as highlighterUtils from '../../../core/highlighter/highlighter-utils';
import * as chargeConverters from '../../../core/charges/charge-converters';
import * as chargeSummarizer from '../../../core/charges/charge-summarizer';
import * as chargeErrors from '../../../core/charges/charge-errors';
import * as cmxArtifacts from '../../../core/artifacts/index';
import * as cmxTypeguards from '../../../typeguards/index';
import * as gradeBenchPanelActions from '../../../actions/grade-bench-panel-actions';
import * as Utils from '../../../core/artifacts/utils';
import * as vitalware from '../../../core/vitalware';
import { readBuildEnv } from '../../build-env';

import {
  clearSearchResults,
  codeSearch,
  featureSelection,
  optimisticCodeChange,
  popupClose,
  popupNavigate,
  cancelInvoicesPopup,
  cancelInvoicesDismiss,
  fetchActivity,
  promptClose,
  navigateDetail,
  openAddChargeModal,
  closeAddChargeModal,
  showAddChargeLoading,
  showSubmittedFeedback,
  showLoadingSubmission,
  showLoadingCancelation,
  hideLoadingSubmission,
  hideSubmittedFeedback,
  showCanceledFeedback,
  submittedFeedbackTimer,
  selectInvoiceToAdd,
  showSizeExpWarning,
  showvitalwareIssuesModal,
  hidevitalwareIssuesModal,
  setvitalwareIssues,
  hideSizeExpWarning,
  deleteSizeExpWarning,
  optimisticProTechChange,
  setLoadingTechPro,
  showHoldCaseModal,
  hideHoldCaseModal,
  showExclusionCaseModal,
  hideExclusionCaseModal,
  getFlagItems,
  getCaseVersions,
  toggleSubmitOldCaseModal,
  showSideNavBar,
  hideSideNavBar,
  showCaseCommentModal,
  hideCaseCommentModal,
  caseOrganization,
  getDocumentsAsHtml,
  updateTransaction,
  updateTransactionOptimistically,
  createTransactionOptimistically,
  replaceOptimisticTransaction,
  docMetadataAvailable,
  setActiveNote,
  fetchCodeBenchLayoutConfiguration,
  setCodeBenchPropertyConfig,
  updateRelatedCasesFilterSelection,
  caseOpenSuccess,
  caseOpenConflict,
  resetCaseAccess,
  setLockCaseUid,
  setBenchMode,
  refreshHoldSubmission,
  codeDescriptionsAvailable
} from '../../../actions/code-bench.actions';
import {
  caseRunAvailable,
  caseRunWithRefreshAvailable,
  caseSearchAvailable,
  userDetailsAvailable,
  loadCaseFromId,
  tokenAvailable,
  hostCaseId,
  loadFromURLMatch
} from '../../../actions/launch-page.action';
import { sessionActivityPrompt } from '../../../actions/session.actions';
import { activationProcessor } from '../../../core/evidence2/activation-processor';
import { EMPTY_NOTE } from '../../../core/amplify/fixture/note';
import { codeBenchState } from '../../../core/amplify/state/code-bench';
import { LOAD_STATES } from '../../load-states';
import { PatientAmplifyEncounterColumns } from '../../../core/encounter/encounter-data';
import { PatientAmplifyEncounterColumns as PatientAmplifyEncounterColumnsv2 } from '../../../core/encounter/encounter-datav2';
import * as AmplifyProcessStates from '../../../core/amplify/process-states';
import * as caseUtils from '../../../core/case/case-utils';
import { getInvoiceOptions } from '../actions/invoice-editor.actions';
import {
  fetchAllInvoices,
  getHoldCaseForProcessInstance,
  getCasesWithEncounter,
  loadRoles,
  updateProcessInstance,
  getCCICustomClaimCrossCases,
  fetchNoteTypes,
  showSplitCaseModal,
  hideSplitCaseModal,
  setActiveMiniTab,
  setActiveVerticalMiniTab,
  setUnresolvedDocuments,
  createTransactionAndAddCode,
  codesAdd,
  codesRemove,
  getCodesWithEncounter,
  fetchProviderRoleTypeConfigurations,
  fetchSourceInvoices,
  transactionOrderChange,
  fetchRelateCaseFilterDictionaries,
  toggleLateCaseClearModal,
  fetchProcessInstance
} from '../../../actions/code-bench.actions';
import { resetCommonState } from '../../../actions/ui.actions';
import { isIPCNote } from '../../../typeguards';
import { isIPC } from '../../service-lines';
import { convertBase64 } from '../../notes/notes';
import {
  determineBenchEditable,
  determineProcessStatus
} from '../utils/bench-editable';
import { lookupCaseDisplayStatus } from '../utils/status-utils';
import { splitCase } from '../../../actions/merge-cases.actions';
import { cloneDeep, isEmpty } from '../../../core/object-utils';
import { convertKeysToSnakeCase } from '../../strings/toSnakeCase';
import { BenchMode, CaseAccessState, SugggestedCodeType } from '../../../enums';
import { convertToFilterRecord } from '../utils/common-filter-utils';
import { isResourceFullAccess } from '../../../core/amplify/resources';
import { removeTransaction, saveInvoice, createInvoice } from '../../../core/amplify/actions/invoice-editor.actions';
import { searchCache } from '../../../core/amplify/typeahead-cache/caches';
import * as transactionUpdateDTOFactory from '../../../core/amplify/transaction-updates.dto-factory';
const {
  getTransactionFromChange,
  isICDChange,
  isModifierChange
} = transactionUpdateDTOFactory;


const CODER_SET = new Set([
  commonEnums.CaseArtifactTypes.PROCESSED,
  commonEnums.CaseArtifactTypes.SUBMITTED,
  commonEnums.CaseArtifactTypes.CODED
]);

const {
  InvoiceStatus,
  InvoiceTypes,
  ChargeSessionCodeType,
  ChargeSessionCodeProvenance,
  CodeSearchType
} = commonEnums;

// This copy/pasted code will be removed in
// https://codametrix.atlassian.net/browse/SURG-188
const getNotes = (caseRun: CMxAPI.CaseRun) => {
  const { notes } = caseRun;
  return decorateNotes(notes as CMxAPI.Note[]);
};

export const decorateNotes = (notes: CMxAPI.Note[]) => {
  return notes.map(note => {
    const withDecodedContent =
      isIPCNote(note) &&
      note.encoding === 'base64' &&
      note.type !== 'text/html'
        ? { ...note, content: convertBase64(note.content) }
        : note;

    const segments = highlighterUtils.segmentize(withDecodedContent);
    return documentUtils.decoratedNoteFactory({
      ...withDecodedContent,
      segments,
      spans: []
    });
  });
};

type ProcessInstanceResult = {
  result: AmplifyAPI.ProcessInstance;
};

const parseSuggestedCodes = (
  suggestedResult: CMxAPI.SuggestedCodeResponse,
  previousSuggestedCodes: CMxCommonApp.SuggestedCodes,
  codeType: string
): CMxCommonApp.SuggestedCodes => {
  const { procedure_codes, modifier_codes, diagnosis_codes } = suggestedResult;
  const getFilteredCodes = (codes: any[]) => ({
    encounterList:
      codes?.filter(
        c => c?.suggested_code_type === SugggestedCodeType.RELATED_CASE
      ) ?? [],
    caseList:
      codes?.filter(
        c => c?.suggested_code_type === SugggestedCodeType.CURRENT_CASE
      ) ?? [],
    codeSourceList:
      codes?.filter(
        c => c?.suggested_code_type === SugggestedCodeType.FROM_SOURCES
      ) ?? []
  });
  const suggestedCodes: CMxCommonApp.SuggestedCodes = {
    cpt:
      codeType === CodeSearchType.CPT
        ? {
            encounterList: [...procedure_codes],
            caseList: [],
            codeSourceList: []
          }
        : previousSuggestedCodes?.cpt,
    modifier:
      codeType === CodeSearchType.MODIFIER
        ? getFilteredCodes(modifier_codes)
        : previousSuggestedCodes?.modifier,
    icd:
      codeType === CodeSearchType.ICD
        ? getFilteredCodes(diagnosis_codes)
        : previousSuggestedCodes?.icd
  };
  return suggestedCodes;
};

const updateCodes = (
  state,
  { result: processInstanceFromAction }: ProcessInstanceResult
) => {
  if (!processInstanceFromAction) {
    return { ...state };
  }

  // Never mutate action.payload / frozen Immer references — helpers assign to processInstance.
  let processInstance = cloneDeep(processInstanceFromAction) as PI;

  const formatedV2Errors =
    vitalware.parseAndFilterV2Errors(processInstance?.errors) ?? [];
  const errorList = [...processInstance.errors, ...formatedV2Errors];

  // default value for isChargeable is true
  if (processInstance?.isChargeable === null) {
    processInstance.isChargeable = true;
  }
  // error handling.
  const codeEdits = vitalware.parseProcessInstance(processInstance);
  let claimEditErrors: AmplifyAPI.AmplifyError[] =
    errorList?.filter(
      error => error.message_key === commonEnums.IssueTypeErrors.CLAIMS_EDIT
    ) || [];

  let cciEditErrors: AmplifyAPI.AmplifyError[] =
    errorList?.filter(
      error => error.message_key === commonEnums.IssueTypeErrors.CCI_EDIT
    ) || [];

  const customRuleErrors: AmplifyAPI.AmplifyError[] =
    errorList?.filter(
      error => error.message_key === commonEnums.IssueTypeErrors.CUSTOM_RULES
    ) || [];

  const parsedClaimEditErrors = <vitalware.CodeIssueMap[]>(
    claimEditErrors.map(editError =>
      vitalware.normalizeClaims(vitalware.parseVWIssues(editError.message))
    )
  );

  const parsedCciEditErrors = <vitalware.CodeIssueMap[]>(
    cciEditErrors.map(editError =>
      vitalware.normalizeCCIS(vitalware.parseVWIssues(editError.message))
    )
  );
  const parsedCustomRuleErrors = <vitalware.CodeIssueMap[]>(
    customRuleErrors.map(editError =>
      vitalware.normalizeCustomRules(vitalware.parseVWIssues(editError.message))
    )
  );

  const vitalwareIssues = vitalware.parseVWByStatus([
    ...parsedClaimEditErrors,
    ...parsedCciEditErrors,
    ...parsedCustomRuleErrors
  ]);

  const { unresolved_documents, hold_issue } = vitalware.parseInvoiceIssues(
    processInstance
  );

  // synthetically create issues for vitalware
  const issues = vitalware.createSyntheticStandardErrors(processInstance);

  const filteredIssues = vitalware.filterInvoiceIssues(processInstance as any);
  const mergeSplitHistoryIssues = vitalware.filterMergeHistoryWarnings(
    processInstance as any
  );

  processInstance.errors = [...filteredIssues, ...issues];

  processInstance = chargeErrors.setErrors(
    processInstance?.errors || [],
    processInstance
  );

  let codableInvoices = chargeConverters.convertInvoicesToCodable(
    processInstance,
    state.codeEdits
  );

  codableInvoices = chargeConverters.addDescriptionsToCodableInvoices(
    codableInvoices,
    state.codeDescriptions
  );

  codableInvoices = updateFieldDefinitions(codableInvoices);

  codableInvoices = chargeConverters.addCategoricalDataToCodableInvoices(
    codableInvoices,
    state.activeItem
  );

  let chargeSessions = chargeConverters.convertInvoicesToCharges(
    processInstance,
    state.codeEdits
  );
  const counts = chargeSummarizer.summarize(chargeSessions);
  const benchEditable = determineBenchEditable(
    processInstance,
    state.userContext,
    state.roles,
    state.activeItem?.parent_caserun_uid,
    state.submitInProgress
  );

  if (isIPC(state?.activeItem?.service_line)) {
    processInstance.caseType = commonEnums.UIModesEnum.STANDALONE;
  }

  return {
    ...state,
    benchEditable,
    processInstance: processInstance, // store the raw response.
    codableInvoices: codableInvoices,
    preCommitChargeSessions: [],
    codeEdits,
    vitalwareIssues,
    mergeSplitHistoryIssues,
    counts,
    documentImprovementIssues: unresolved_documents,
    holdIssues: hold_issue
  };
};

const updateFieldDefinitions = (
  codableInvoices: CMxCommonApp.CodableInvoice[]
) => {
  return [...codableInvoices].map(codableInvoice => {
    codableInvoice.transactions = codableInvoice.transactions.map(
      (txn: CMxCommonApp.CodableTransaction) => {
        if (codableInvoice.type === InvoiceTypes.SHARED_CODES) {
          return {
            ...txn,
            fieldDefinitions: [{ label: 'Diagnosis', value: 'ICD', key: 'ICD' }]
          };
        }
        const hasProcedures = txn.codes
          .filter(cmxTypeguards.isChargeSessionCode)
          .some(
            code =>
              code.codeType === ChargeSessionCodeType.PROCEDURE &&
              code.provenance === ChargeSessionCodeProvenance.CPT
          );
        const fieldDefinitions = [
          {
            label: 'Procedure',
            value: 'CPT',
            key: 'CPT',
            disabled: hasProcedures
          },
          { label: 'Diagnosis', value: 'ICD', key: 'ICD' },
          { label: 'Modifier', value: 'MODIFIER', key: 'MODIFIER' }
        ];

        return { ...txn, fieldDefinitions };
      }
    );

    return codableInvoice;
  });
};

const pessimisticallyRevert = (state, res) => {
  const s = { ...state };
  s.chargeSessions = [...state.preCommitChargeSessions];
  s.preCommitChargeSessions = [];
  return s;
};

const handleOptmisticChange = (
  state: Accelerate.BenchApplicationState,
  optimisticChange: Accelerate.OptimisticChange
) => {
  const originalState = { ...state };
  // deep clone to deal only with a fresh data structure
  const { codeChange, updateDTO } = JSON.parse(
    JSON.stringify(optimisticChange)
  );
  const processInstance: AmplifyAPI.ProcessInstance = { ...originalState.processInstance };

  const preCommitChargeSessions = [];
  processInstance.invoices = codeChange.context.invoices;
  const transaction = getTransactionFromChange(codeChange);

  // APPLY THE CODE CHANGE IN PLACE.
  // If the change fails, the preCommitChargeSessions
  // will be used to revert to the previous state.
  // THIS ONLY HANDLES CODE SWAPS ATM, SEARCH DROPS WILL
  // TO BE HANDLED AT THAT TIME.
  if (isICDChange(codeChange)) {
    transaction.diagnoses = updateDTO.diagnoses.filter(
      item => Object.keys(item).length > 0
    );
  } else if (isModifierChange(codeChange)) {
    transaction.modifiers = updateDTO.modifiers.filter(
      item => Object.keys(item).length > 0
    );
  } else {
    return originalState;
  }

  const updatedState = updateCodes(originalState, { result: processInstance });

  return {
    ...updatedState,
    preCommitChargeSessions
  };
};

const updateConcurrentCaseAccessBenchEditable = (
  state: Accelerate.BenchApplicationState,
  payload: any,
  isCaseTakenByAnotherUser: boolean
) => {
  // Environment variable: ALLOW_CONCURRENT_CASE_EDIT
  // If set to "true" (or not defined), allows editing a case even if it is already opened by another user.
  // If set to "false", such cases will be read-only.
  const isConcurrentCaseEditAllowed =
    readBuildEnv('ALLOW_CONCURRENT_CASE_EDIT') == 'true';

  return determineBenchEditable(
    payload,
    state.userContext,
    state.roles,
    state.activeItem?.parent_caserun_uid,
    state.submitInProgress,
    !isCaseTakenByAnotherUser || isConcurrentCaseEditAllowed
  );
};

// Convenience alias used below
type PI = AmplifyAPI.ProcessInstance;

const initialState = codeBenchState;

const codeBench = createReducer(initialState, builder => {
  builder.addCase(resetCommonState.type, () => ({ ...initialState }));

  builder.addCase('CODE_UPDATE/rejected', pessimisticallyRevert);

  builder.addCase('INVOICE_SUBMITTED/pending', (state, action: AnyAction) => {
    const submitInProgress = true;
    const { processInstance } = action.meta.arg as AmplifyAPI.Submission;
    const benchEditable = determineBenchEditable(
      processInstance,
      state.userContext,
      state.roles,
      state.activeItem?.parent_caserun_uid,
      submitInProgress
    );
    return {
      ...state,
      submitInProgress,
      benchEditable
    };
  });

  builder.addCase('INVOICE_SUBMITTED/rejected', (state, action: AnyAction) => {
    const submitInProgress = false;
    const { processInstance } = action.meta.arg as AmplifyAPI.Submission;
    const benchEditable = determineBenchEditable(
      processInstance,
      state.userContext,
      state.roles,
      state.activeItem?.parent_caserun_uid,
      submitInProgress
    );
    return {
      ...state,
      submitInProgress,
      benchEditable
    };
  });

  builder.addCase('INVOICE_SUBMITTED/fulfilled', (state, action: AnyAction) => {
    const processInstance = action.payload as PI;
    return updateCodes({ ...state, submitInProgress: false }, { result: processInstance });
  });

  builder.addCase('CODE_UPDATE/fulfilled', (state, action: AnyAction) => {
    return updateCodes(state, { result: action.payload as PI });
  });

  builder.addCase('CODE_ADD/fulfilled', (state, action: AnyAction) => {
    return updateCodes(state, { result: action.payload as PI });
  });

  builder.addCase('BULK_CODE_ADD/fulfilled', (state, action: AnyAction) => {
    return updateCodes(state, { result: action.payload as PI });
  });

  builder.addCase('CODE_REMOVE/fulfilled', (state, action: AnyAction) => {
    return updateCodes(state, { result: action.payload as PI });
  });

  builder.addCase('BULK_CODE_REMOVE/fulfilled', (state, action: AnyAction) => {
    return updateCodes(state, { result: action.payload as PI });
  });

  builder.addCase('TIME_BASED_CODE_ADD/fulfilled', (state, action: AnyAction) => {
    return updateCodes(state, { result: action.payload as PI });
  });

  builder.addCase('CODE_SEARCH_DROP/fulfilled', (state, action: AnyAction) => {
    return updateCodes(state, { result: action.payload as PI });
  });

  builder.addCase('TXN_SHARED_CODE/fulfilled', (state, action: AnyAction) => {
    return updateCodes(state, { result: action.payload as PI });
  });

  builder.addCase('ENABLE_TECH_PRO/fulfilled', (state, action: AnyAction) => {
    return updateCodes(state, { result: action.payload as PI });
  });

  builder.addCase('DISABLE_TECH_PRO/fulfilled', (state, action: AnyAction) => {
    return updateCodes(state, { result: action.payload as PI });
  });

  builder.addCase('TOGGLE_CHARGEABLE_CASE/fulfilled', (state, action: AnyAction) => {
    return updateCodes(state, { result: action.payload as PI });
  });

  builder.addCase('TRANSACTION_ORDER_CHANGE/fulfilled', (state, action: AnyAction) => {
    return updateCodes(state, { result: action.payload as PI });
  });

  builder.addCase(removeTransaction.fulfilled, (state, action) => {
    return updateCodes(state, { result: action.payload });
  });

  builder.addCase('CODE_ADD/pending', (state, action: AnyAction) => {
    // selecting a code to add wipes out the search results for
    // that term.
    const transactionGuid = (action.meta.arg as Accelerate.CodeAddActionPayload)?.context?.transaction?.guid ?? '';
    const newResults = {
      ...state.searchResults
    };
    newResults[transactionGuid] = [];

    return {
      ...state,
      searchResults: newResults
    };
  });

  builder.addCase('CODE_SEARCH_DROP/pending', (state, action: AnyAction) => {
    // selecting a code to add wipes out the search results for
    // that term.
    const transactionGuid = (action.meta.arg as Accelerate.CodeChange)?.context?.transaction?.guid ?? '';
    const newResults = {
      ...state.searchResults
    };
    newResults[transactionGuid] = [];

    return {
      ...state,
      searchResults: newResults
    };
  });

  builder.addCase(tokenAvailable.pending, state => {
    return { ...codeBenchState };
  });

  builder.addCase(loadCaseFromId.pending, state => {
    const { hostCaseId, userContext, roles, showSideNavBar } = state;
    const caseAccessTime = cmxDateTime.format(
      new Date().toUTCString().replace('GMT', '[GMT]'),
      cmxDateTime.FORMATS.DATE_TIME_WITHOUT_TIMEZONE
    );
    return {
      ...codeBenchState,
      hostCaseId,
      userContext,
      roles,
      caseAccessTime,
      showSideNavBar
    };
  });

  builder.addCase(loadFromURLMatch.pending, state => {
    const { hostCaseId, userContext, roles, showSideNavBar } = state;
    const caseAccessTime = cmxDateTime.format(
      new Date().toUTCString().replace('GMT', '[GMT]'),
      cmxDateTime.FORMATS.DATE_TIME_WITHOUT_TIMEZONE
    );
    return {
      ...codeBenchState,
      hostCaseId,
      userContext,
      roles,
      caseAccessTime,
      showSideNavBar
    };
  });

  builder.addCase(getInvoiceOptions.fulfilled, (state, action) => {
    const invoiceOptions = action.payload;
    return {
      ...state,
      invoiceOptions
    };
  });

  builder.addCase('ALL_INVOICES/fulfilled', (state, action: AnyAction) => {
    const invoiceOptions = action.payload as AmplifyAPI.InvoiceOptionsDTO;
    return {
      ...state,
      invoiceOptions
    };
  });

  builder.addCase('POPUP_NAVIGATE', (state, action: AnyAction) => {
    const caseRequest = action.payload;
    return {
      ...state,
      modalURL: {
        path: `/accelerate/popup`,
        //@ts-ignore
        queryString: `?${new URLSearchParams({
          caseId: caseRequest.caseId,
          hostCaseId: `${caseRequest.hostCaseId}`,
          popup: 'true',
          requestToken: 'true',
          serviceLine: caseRequest.serviceLine
        })}`
      }
    };
  });

  builder.addCase('POPUP_CLOSE', state => {
    return {
      ...state,
      modalURL: { path: '' }
    };
  });

  builder.addCase('CANCEL_INVOICES_POPUP', state => {
    return {
      ...state,
      displayCancellation: true
    };
  });

  builder.addCase('OPEN_ADD_CHARGE_MODAL', state => {
    return {
      ...state,
      displayAddCharge: true
    };
  });

  builder.addCase('CLOSE_ADD_CHARGE_MODAL', state => {
    return {
      ...state,
      displayAddCharge: false
    };
  });

  builder.addCase('ADD_TRANSACTION_LOADING', state => {
    return {
      ...state,
      showAddChargeLoading: true
    };
  });

  builder.addCase('SELECT_INVOICE_TO_ADD', state => {
    return {
      ...state,
      displayAddCharge: false
    };
  });

  builder.addCase('ADD_TRANSACTION/rejected', state => {
    return {
      ...state,
      showAddChargeError: true,
      showAddChargeLoading: false
    };
  });

  builder.addCase('ADD_TRANSACTION/fulfilled', state => {
    return {
      ...state,
      displayAddCharge: false,
      showAddChargeLoading: false
    };
  });

  builder.addCase('SHOW_SUBMITTED_FEEDBACK', state => {
    return {
      ...state,
      showSubmittedFeedback: true,
      showLoadingSubmission: false,
      showvitalwareIssuesModal: false
    };
  });

  builder.addCase('SHOW_LOADING_SUBMISSION', state => {
    return {
      ...state,
      showLoadingSubmission: true
    };
  });

  builder.addCase('SHOW_LOADING_CANCELATION', state => {
    return {
      ...state,
      showLoadingCancelation: true
    };
  });

  builder.addCase('SHOW_CANCELED_MODAL', state => {
    return {
      ...state,
      showCanceledFeedback: true,
      showLoadingCancelation: false
    };
  });

  builder.addCase('HIDE_LOADING_SUBMISSION', state => {
    return {
      ...state,
      showLoadingSubmission: false
    };
  });

  builder.addCase('HIDE_SUBMITTED_FEEDBACK', state => {
    return {
      ...state,
      showSubmittedFeedback: false,
      showCanceledFeedback: false
    };
  });

  builder.addCase('SUBMITTED_MODAL_TIMER', state => {
    return {
      ...state,
      submittedFeedbackTimer: true
    };
  });

  builder.addCase('CANCEL_INVOICES_DISMISS', state => {
    return {
      ...state,
      displayCancellation: false
    };
  });

  builder.addCase('SET_BENCH_MODE', (state, action: AnyAction) => {
    const benchMode = action.payload;
    return {
      ...state,
      benchMode
    };
  });

  builder.addCase('CASE_OPEN_SUCCESS', (state, action: AnyAction) => {
    const payload = action.payload;
    const benchEditable =
      state.benchMode === BenchMode.GRADE
        ? state.benchEditable
        : updateConcurrentCaseAccessBenchEditable(state, payload, false);
    return {
      ...state,
      benchEditable,
      caseAccessState: CaseAccessState.ACQUIRED
    };
  });

  builder.addCase('CASE_OPEN_CONFLICT', (state, action: AnyAction) => {
    const payload = action.payload;
    return {
      ...state,
      benchEditable: updateConcurrentCaseAccessBenchEditable(
        state,
        payload,
        true
      ),
      caseAccessState: CaseAccessState.CONFLICT
    };
  });

  builder.addCase(setLockCaseUid.type, (state, action: AnyAction) => {
    return {
      ...state,
      lockCaseUid: action.payload
    };
  });

  builder.addCase('RESET_CASE_ACCESS', (state, action: AnyAction) => {
    const payload = action.payload;
    return {
      ...state,
      benchEditable: updateConcurrentCaseAccessBenchEditable(
        state,
        payload,
        true
      ),
      caseAccessState: CaseAccessState.NONE
    };
  });

  builder.addCase('CANCEL_INVOICES_CONFIRM/fulfilled', (state, action: AnyAction) => {
    const processInstance = action.payload as PI;
    const updatedState = updateCodes(state, { result: processInstance });
    return {
      ...state,
      ...updatedState,
      displayCancellation: false,
      processInstance
    };
  });

  builder.addCase('INVOICE_AVAILABLE/fulfilled', (state, action: AnyAction) => {
    const processInstance = action.payload as PI;
    const updatedState = updateCodes(state, { result: processInstance });
    return {
      ...state,
      ...updatedState
    };
  });

  builder.addCase(saveInvoice.fulfilled, (state, action) => {
    const processInstance = action.payload;
    const updatedState = updateCodes(state, { result: processInstance });
    return {
      ...state,
      ...updatedState
    };
  });

  builder.addCase(createInvoice.fulfilled, (state, action) => {
    const processInstance = action.payload;
    const updatedState = updateCodes(state, { result: processInstance });
    return {
      ...state,
      ...updatedState
    };
  });

  builder.addCase('FETCH_SOURCE_INVOICES/fulfilled', (state, action: AnyAction) => {
    const invoices = action.payload as AmplifyAPI.Invoice[];
    return {
      ...state,
      sourceInvoices: {
        sourceInvoices: invoices,
        sourceInvoicesLoadState: LOAD_STATES.done
      }
    };
  });

  builder.addCase('FETCH_SOURCE_INVOICES/pending', state => {
    return {
      ...state,
      sourceInvoices: {
        sourceInvoices: [],
        sourceInvoicesLoadState: LOAD_STATES.started
      }
    };
  });

  builder.addCase('FETCH_SOURCE_INVOICES/rejected', state => {
    return {
      ...state,
      sourceInvoices: {
        sourceInvoices: [],
        sourceInvoicesLoadState: LOAD_STATES.failed
      }
    };
  });

  builder.addCase('ACTIVITY/fulfilled', (state, action: AnyAction) => {
    const activity = action.payload as (CMxAPI.UserActivity | CMxAPI.SystemActivity | CMxAPI.UserActivityComment)[];
    return { ...state, activity: activity };
  });

  builder.addCase('INVOICE_AVAILABLE/rejected', state => {
    return { ...state, codableInvoices: [] };
  });

  builder.addCase('CODE_SEARCH/fulfilled', (state, action: AnyAction) => {
    const params = action.meta.arg as Accelerate.CodeQuery;
    const searchResponse = action.payload as Accelerate.CodeSearchResponse;
    const existingDescriptions = state.codeDescriptions ?? {};
    // append the search results to a specific charge session.
    const newResults: Accelerate.SearchResultMap = {
      ...state.searchResults
    };

    const newResultsV2: Accelerate.CodeSearchResultMap = {
      ...state.codeSearchResultsV2
    };

    let primaryResultId = '';

    if (searchResponse.codeQuery.context?.transaction?.guid) {
      primaryResultId = searchResponse.codeQuery.context?.transaction?.guid;
    }

    if (searchResponse.codeQuery.context?.transaction?.id) {
      primaryResultId = searchResponse.codeQuery.context?.transaction?.id;
    }

    primaryResultId = primaryResultId || params?.searchResultsLookupKey || '';

    // grab the max from the cache.
    newResults[primaryResultId] = searchCache.getLatest(
      searchResponse
    ).searchResults;

    const existingResultsBySearchType = newResultsV2?.[primaryResultId] ?? {};
    newResultsV2[primaryResultId] = {
      ...existingResultsBySearchType,
      [params?.searchType]: searchCache.getLatest(searchResponse).searchResults
    };

    const codeDescriptions = { ...existingDescriptions };
    searchResponse.searchResults.forEach(code => {
      codeDescriptions[code.code] = codeDescriptions[code.code] || code;
    });

    return {
      ...state,
      searchResults: newResults,
      codeSearchResultsV2: newResultsV2,
      codeDescriptions
    };
  });

  builder.addCase('PARENT_CASE', (state, action: AnyAction) => {
    const caseIds = action.payload;
    return { ...state, hostCaseId: caseIds.hostCaseId };
  });

  builder.addCase('FEATURE_SELECTION', (state, action: AnyAction) => {
    const selection = action.payload;
    const { notes } = state;
    const activations = activationProcessor(
      selection,
      state.activations.panel,
      state.activations.property,
      state.activations.spans
    );

    // Assume we have 0 or 1 notes until surgery or some
    // other situation arises.
    // If we don't have a note, then we use a mock note.
    const note =
      notes.length > 0 ? notes[0] : { ...EMPTY_NOTE };

    const boundedSpans = highlighterUtils.normalizeSpans(
      note.segments,
      activations.spans.body ?? []
    );
    const returnedNotes: CMxCommonApp.DecoratedNote[] = [
      { ...note, spans: boundedSpans }
    ];
    return {
      ...state,
      activations,
      notes: returnedNotes
    };
  });

  builder.addCase('CLEAR_SEARCH', (state, action: AnyAction) => {
    const identifier = action.payload;
    const newResults: Accelerate.SearchResultMap = {
      ...state.searchResults
    };
    newResults[identifier] = [];

    const newResultsV2: Accelerate.CodeSearchResultMap = {
      ...state.codeSearchResultsV2
    };
    newResultsV2[identifier] = {};

    return {
      ...state,
      searchResults: newResults,
      codeSearchResultsV2: newResultsV2
    };
  });

  builder.addCase('CASE_SEARCH', (state, action: AnyAction) => {
    const cases = action.payload;
    return {
      ...state,
      cases
    };
  });

  builder.addCase('CODE_DESCRIPTIONS', (state, action: AnyAction) => {
    const newDescriptions = action.payload;
    const {
      codeDescriptions,
      codableInvoices: originalInvoices,
      sourceInvoices
    } = state;
    const updatedDescriptions = { ...codeDescriptions, ...newDescriptions };

    const codableInvoices = chargeConverters.addDescriptionsToCodableInvoices(
      cloneDeep(originalInvoices || []),
      updatedDescriptions
    );

    return {
      ...state,
      codeDescriptions: updatedDescriptions,
      codableInvoices,
      sourceInvoices
    };
  });

  builder.addCase('BILLING_GROUP', (state, action: AnyAction) => {
    const result = action.payload;
    const { activeItem } = state;
    const groupedCases = caseUtils.getGroupedCases(
      activeItem,
      result.content,
      'billing'
    );

    return {
      ...state,
      billingGroupLoadState: LOAD_STATES.done,
      groupedCases
    };
  });

  builder.addCase('USER_DETAILS', (state, action: AnyAction) => {
    const userDetails = action.payload;
    const userContext = userDetails.contexts.find(
      context => context.organizationId === userDetails.activeContextId
    );
    return {
      ...state,
      userContext
    };
  });

  builder.addCase('ACTIVITY_PROMPT', state => {
    return {
      ...state,
      prompt: {
        text: 'Are you still there?'
      }
    };
  });

  builder.addCase('PROMPT_CLOSE', state => {
    return {
      ...state,
      //@ts-ignore
      prompt: null
    };
  });

  builder.addCase('NAVIGATE_DETAIL/pending', state => {
    const {
      activations,
      artifactSet,
      artifactList,
      propertyConfig,
      codeEdits,
      artifactLoadState,
      loadState,
      processInstance,
      codableInvoices,
      preCommitChargeSessions,
      activity,
      benchEditable,
      counts
    } = codeBenchState;

    return {
      ...state,
      benchEditable,
      activations,
      artifactSet,
      artifactList,
      propertyConfig,
      codeEdits,
      artifactLoadState,
      loadState,
      processInstance,
      codableInvoices,
      preCommitChargeSessions,
      activity,
      billingGroupLoadState: LOAD_STATES.started,
      counts
    };
  });

  builder.addCase('CASE_DETAIL', (state, action: AnyAction) => {
    const activeItem = action.payload;
    const artifactSet = CODER_SET;
    const codeEdits = vitalware.parseCodeEdits(activeItem);
    const notes = getNotes(activeItem);
    let slName = `${activeItem.service_line}`.toLowerCase();
    if (isIPC(slName)) {
      slName = 'inpatientprofessionalcoding';
    }
    const encounterColumns = Utils.doesPathEndWithV2()
      ? PatientAmplifyEncounterColumnsv2
      : PatientAmplifyEncounterColumns;

    // Check if slName exists in the chosen object - this is the fallback
    const fallbackPropertyConfig =
      slName in encounterColumns
        ? encounterColumns[slName]
        : state.propertyConfig;

    // Use existing dynamic config if available, otherwise use fallback
    let propertyConfig = fallbackPropertyConfig;
    if (Utils.doesPathEndWithV2()) {
      // V2 Dynamic Configuration will be added at a later date
    } else {
      propertyConfig = state.dynamicPropertyConfig || fallbackPropertyConfig;
    }

    const mixin: any = {};
    // IPC Notes come from a separate process and are not included fully on
    // the case
    if (!isIPC(activeItem?.service_line)) {
      let newNotes = [...notes];
      newNotes = newNotes.filter(note => note.document_id !== null);

      newNotes.sort(documentUtils.compareDecoratedNotesForNav);
      mixin.notes = newNotes;
    }

    return {
      ...state,
      ...mixin,
      activeItem,
      artifactSet,
      propertyConfig,
      fallbackPropertyConfig,
      codeEdits,
      artifactLoadState: LOAD_STATES.done,
      loadState: LOAD_STATES.done
    };
  });

  builder.addCase('CASE_DETAIL_WITH_REFRESH', (state, action: AnyAction) => {
    const activeItemWithRefresh = action.payload;
    /**
     * Currently updating latest patient_class and patient_type from XC
     * In future we could add more props
     */
    let activeItem = state.activeItem;
    if (!!activeItem) {
      activeItem = {
        ...activeItem,
        patient_class:
          activeItemWithRefresh?.patient_class ?? activeItem?.patient_class,
        patient_type:
          activeItemWithRefresh?.patient_type ?? activeItem?.patient_type
      };
    }
    return {
      ...state,
      activeItem
    };
  });

  builder.addCase('SHOW_SIZE_EXP_WARNING', state => {
    return {
      ...state,
      showSizeExpWarning: state.showSizeExpWarning === null ? false : true
    };
  });

  builder.addCase('HIDE_SIZE_EXP_WARNING', state => {
    return {
      ...state,
      showSizeExpWarning: state.showSizeExpWarning === null ? false : false
    };
  });

  builder.addCase('DELETE_SIZE_EXP_WARNING', state => {
    return {
      ...state,
      showSizeExpWarning: false
    };
  });

  builder.addCase('OPTIMISTIC_PRO_TECH_CHANGE', (state, action: AnyAction) => {
    const techCodableTransaction = action.payload;
    if (techCodableTransaction === undefined) return { ...state };

    // store invoices in constant, using 'state' object
    const invoices = cloneDeep(state.processInstance.invoices);

    // find invoice index with matching guid
    const invoiceIndex = invoices.findIndex(
      invoice => invoice.guid === techCodableTransaction?.invoice?.guid
    );

    // find transaction where code matches either the mapped procedure code or technical code
    const transaction = invoices[invoiceIndex].transactions.find(
      transaction => transaction.guid === techCodableTransaction.transactionGuid
    );

    if (transaction !== null && transaction !== undefined) {
      // check if mapped procedure property exists and change active state
      if (Object(techCodableTransaction).hasOwnProperty('mappedProcedure')) {
        //@ts-ignore
        transaction.mapped_procedure.active = !transaction.mapped_procedure
          .active;

        // else check for technical code property and change active state
      } else if (
        Object(techCodableTransaction).hasOwnProperty('technicalCode')
      ) {
        //@ts-ignore
        transaction.technical_code.active = !transaction.technical_code.active;
      }
    }

    // update codes using spread operator
    const updatedState = updateCodes(state, {
      result: { ...state.processInstance, invoices }
    });

    return {
      ...state,
      ...updatedState
    };
  });

  builder.addCase('SET_LOADING_TECH_PRO', (state, action: AnyAction) => {
    const options = action.payload;
    // store invoices in constant, using 'state' object
    const invoices = cloneDeep(state.processInstance.invoices);

    // find invoice index with matching guid
    const invoiceIndex = invoices.findIndex(
      invoice => invoice.guid === options.guid
    );

    // set loading state to invoice
    invoices[invoiceIndex].isLoadingTechPro = options.value;

    // update codes using spread operator
    const updatedState = updateCodes(state, {
      result: { ...state.processInstance, invoices }
    });

    return {
      ...state,
      ...updatedState
    };
  });

  builder.addCase('SHOW_CLAIMS_ISSUES_MODAL', state => {
    return {
      ...state,
      showvitalwareIssuesModal: true
    };
  });

  builder.addCase('HIDE_CLAIMS_ISSUES_MODAL', state => {
    return {
      ...state,
      showvitalwareIssuesModal: false
    };
  });

  builder.addCase('SET_CLAIMS_ISSUES', (state, action: AnyAction) => {
    const vitalwareIssuesArray = action.payload;
    return {
      ...state,
      vitalwareIssues: vitalware.parseVWByStatus(vitalwareIssuesArray)
    };
  });

  builder.addCase('HIDE_HOLD_CASE_MODAL', state => {
    return {
      ...state,
      toggleHoldCaseModal: false
    };
  });

  builder.addCase('SHOW_HOLD_CASE_MODAL', state => {
    return {
      ...state,
      toggleHoldCaseModal: true
    };
  });

  builder.addCase('SHOW_CASE_COMMENT_MODAL', state => {
    return {
      ...state,
      toggleCaseCommentModal: true
    };
  });

  builder.addCase('HIDE_CASE_COMMENT_MODAL', state => {
    return {
      ...state,
      toggleCaseCommentModal: false
    };
  });

  builder.addCase('HIDE_EXCLUSION_CASE_MODAL', state => {
    return {
      ...state,
      toggleExclusionCaseModal: false
    };
  });

  builder.addCase('TOGGLE_LATE_CASE_CLEAR_MODAL', (state, action: AnyAction) => {
    const toggle = action.payload;
    return {
      ...state,
      showLateCaseClearModal: toggle
    };
  });

  builder.addCase('SHOW_EXCLUSION_CASE_MODAL', state => {
    return {
      ...state,
      toggleExclusionCaseModal: true
    };
  });

  builder.addCase('SHOW_SPLIT_CASE_MODAL', state => {
    return {
      ...state,
      toggleSplitCaseModal: true
    };
  });

  builder.addCase('HIDE_SPLIT_CASE_MODAL', state => {
    return {
      ...state,
      toggleSplitCaseModal: false
    };
  });

  builder.addCase('TOGGLE_SUBMIT_OLD_CASE_MODAL', state => {
    return {
      ...state,
      submitOldCaseModal: !state.submitOldCaseModal
    };
  });

  builder.addCase('SHOW_SIDE_NAV_BAR', state => {
    return {
      ...state,
      showSideNavBar: true
    };
  });

  builder.addCase('HIDE_SIDE_NAV_BAR', state => {
    return {
      ...state,
      showSideNavBar: false
    };
  });

  builder.addCase('FETCH_HOLD_CASE/fulfilled', (state, action: AnyAction) => {
    const holdCaseStatus = action.payload as CMxAPI.HoldCaseResponse;
    return {
      ...state,
      holdCaseStatus
    };
  });

  builder.addCase('FETCH_CASE_VERSIONS/pending', state => {
    return {
      ...state,
      caseVersions: []
    };
  });

  builder.addCase('FETCH_CASE_VERSIONS/fulfilled', (state, action: AnyAction) => {
    const caseVersions = action.payload as AmplifyAPI.CaseVersion[];
    return {
      ...state,
      caseVersions
    };
  });

  builder.addCase('FETCH_CASES_WITH_ENCOUNTER/pending', state => {
    return {
      ...state,
      relatedCasesLoadState: LOAD_STATES.initial
    };
  });

  builder.addCase('FETCH_CASES_WITH_ENCOUNTER/rejected', state => {
    return {
      ...state,
      relatedCasesLoadState: LOAD_STATES.failed,
      relatedCases: [...codeBenchState.relatedCases]
    };
  });

  builder.addCase(getCasesWithEncounter.fulfilled, (state, action) => {
    const result = action.payload;
    if (!result) {
      return {
        ...state,
        relatedCasesLoadState: LOAD_STATES.failed
      };
    }
    const { roles, userContext } = state;
    const { content = [] } = result;
    const sp: CMxCommonApp.SortablePageable<any> = {
      ...state.relatedCasesPageable,
      pageableDefinition: result
    };

    const relatedCases: CMxCommonApp.RelatedCaseExtended[] = content.map(
      item => {
        const chargeable =
          typeof item?.chargeable_indicator === 'string'
            ? item?.chargeable_indicator === 'true'
            : Boolean(item?.chargeable_indicator);
        const displayStatus = lookupCaseDisplayStatus(
          item?.service_line,
          chargeable,
          item?.case_status,
          item?.process_status,
          item?.coding_status?.process_instance_status,
          item?.coding_status?.qa_status,
          item?.coding_status?.hold_status
        );
        if (!!displayStatus) {
          return {
            ...item,
            processStatus: displayStatus
          };
        }

        const processStatus = determineProcessStatus(
          {
            isChargeable: item?.coding_status?.is_chargeable,
            processInstanceStatus: item?.coding_status?.process_instance_status,
            holdReason: item?.coding_status?.hold_status,
            holdComment: item?.coding_status?.hold_comment,
            qaStatus: item?.coding_status?.qa_status,
            cancelled: item?.coding_status?.is_cancelled,
            roles,
            userContext
          },
          item?.coding_status !== null,
          null
        );
        return {
          ...item,
          processStatus
        };
      }
    );

    return {
      ...state,
      relatedCases,
      relatedCasesPageable: sp,
      relatedCasesLoadState: LOAD_STATES.done
    };
  });

  builder.addCase('FETCH_CCI_CUSTOM_CLAIM_CROSS_CASES/pending', state => {
    return {
      ...state,
      customClaimsRelatedCasesLoadState: LOAD_STATES.initial
    };
  });

  builder.addCase('FETCH_CCI_CUSTOM_CLAIM_CROSS_CASES/rejected', state => {
    return {
      ...state,
      customClaimsRelatedCasesLoadState: LOAD_STATES.failed,
      customClaimsRelatedCases: [
        ...codeBenchState.customClaimsRelatedCases
      ]
    };
  });

  builder.addCase('FETCH_CCI_CUSTOM_CLAIM_CROSS_CASES/fulfilled', (state, action: AnyAction) => {
    const result = action.payload as CMxAPI.CaseRun[];
    // Filter out items with the same case_id as activeItem case_id
    const filteredResult = result?.filter(
      item => item.case_id !== state.activeItem.case_id
    );
    return {
      ...state,
      customClaimsRelatedCases: filteredResult,
      customClaimsRelatedCasesLoadState: LOAD_STATES.done
    };
  });

  builder.addCase('ORGANIZATION_AVAILABLE', (state, action: AnyAction) => {
    const organization = action.payload;
    //@ts-ignore
    return { ...state, organization };
  });

  builder.addCase('GET_HTML/fulfilled', (state, action: AnyAction) => {
    const htmlNotes = action.payload as CMxCommonApp.DecoratedNote[];
    let newNotes = [...htmlNotes]
      .filter(note => note.document_id !== null)
      .map(documentUtils.shimSegmentsIfNeeded);

    newNotes.sort(documentUtils.compareDecoratedNotesForNav);

    const sortedNotesDocIds = newNotes.map(note => note.document_id);
    const metadataByDocId = new Map(
      (state.documentMetadata ?? []).map(doc => [doc.documentId, doc])
    );
    const seenDocIds = new Set<number>();
    const newDocumentMetadata: CMxAPI.DocumentMetadata[] = [];
    for (const docId of sortedNotesDocIds) {
      if (docId == null) continue;
      const meta = metadataByDocId.get(docId);
      if (meta && !seenDocIds.has(docId)) {
        newDocumentMetadata.push(meta);
        seenDocIds.add(docId);
      }
    }
    for (const doc of state.documentMetadata ?? []) {
      if (!seenDocIds.has(doc.documentId)) {
        newDocumentMetadata.push(doc);
      }
    }

    return {
      ...state,
      notes: newNotes,
      activeNote: newNotes[0],
      documentMetadata: newDocumentMetadata
    };
  });

  builder.addCase('DOCUMENT_METADATA', (state, action: AnyAction) => {
    const docs = action.payload;
    return {
      ...state,
      documentMetadata: docs
    };
  });

  builder.addCase('SET_ACTIVE_NOTE', (state, action: AnyAction) => {
    const activeNote = action.payload;
    return {
      ...state,
      activeNote
    };
  });

  builder.addCase('LOAD_ROLES', (state, action: AnyAction) => {
    const roles = action.payload;
    return {
      ...state,
      roles
    };
  });

  builder.addCase('FETCH_FLAGS/fulfilled', (state, action: AnyAction) => {
    const flagList = action.payload as CMxAPI.FlagItem[];
    return {
      ...state,
      flagList
    };
  });

  builder.addCase('UPDATE_PROCESS_INSTANCE/fulfilled', (state, action: AnyAction) => {
    const processInstance = action.payload as PI;
    const updatedState = updateCodes(state, { result: processInstance });
    return {
      ...state,
      ...updatedState,
      processInstance
    };
  });

  builder.addCase('FETCH_NOTE_TYPES/fulfilled', (state, action: AnyAction) => {
    const dictionaryValues = action.payload as Accelerate.DictionaryRowData<CMxCommonApp.NoteTypeRow>;
    return {
      ...state,
      noteTypes: dictionaryValues?.rowValues ?? []
    };
  });

  builder.addCase('PROVIDER_ROLETYPE_CONFIGURATION/fulfilled', (state, action: AnyAction) => {
    const dictionaryValues = action.payload as Accelerate.DictionaryRowData<CMxCommonApp.ProviderRoleTypeConfigRow>;
    return {
      ...state,
      providerRoleTypeConfig: dictionaryValues?.rowValues ?? []
    };
  });

  builder.addCase('SPLIT_CASE/rejected', state => {
    return {
      ...state,
      toggleSplitCaseModal: false
    };
  });

  builder.addCase('SET_ACTIVE_MINI_TAB', (state, action: AnyAction) => {
    const tabName = action.payload;
    const activeMiniTab = new Set<String>(
      Object.values(commonEnums.MiniTabLabels)
    ).has(tabName)
      ? tabName
      : commonEnums.MiniTabLabels.CHARGES_LABEL;
    return {
      ...state,
      activeMiniTab
    };
  });

  builder.addCase('SET_ACTIVE_VERTICAL_MINI_TAB', (state, action: AnyAction) => {
    const tabName = action.payload;
    const activeVerticalMiniTab = new Set<String>(
      Object.values(commonEnums.MiniTabLabels)
    ).has(tabName)
      ? tabName
      : commonEnums.MiniTabLabels.NOTES_LABEL;
    return {
      ...state,
      activeVerticalMiniTab
    };
  });

  builder.addCase('SET_UNRESOLVED_DOCUMENTS', (state, action: AnyAction) => {
    const showUnresolvedDocuments = action.payload;
    return {
      ...state,
      showUnresolvedDocuments
    };
  });

  builder.addCase('SPLIT_CASE/fulfilled', (state, action: AnyAction) => {
    const processInstance = action.payload as PI;
    const updatedState = updateCodes(state, { result: processInstance });
    return {
      ...state,
      ...updatedState,
      processInstance,
      toggleSplitCaseModal: false
    };
  });

  builder.addCase('UPDATE_TRANSACTION', (state, action: AnyAction) => {
    const payload = action.payload;
    const { transaction, invoiceGuid } = payload;
    const invoices = cloneDeep(state.processInstance?.invoices || null);
    const invoiceIndex = invoices
      ? invoices.findIndex(inv => inv.guid === invoiceGuid)
      : -1;

    if (invoiceIndex !== -1) {
      const transactionIndex = invoices[invoiceIndex].transactions?.findIndex(
        txn => txn.guid === transaction.guid
      );
      if (transactionIndex !== -1) {
        invoices[invoiceIndex].transactions[transactionIndex] = transaction;
      }
    }
    const updatedResult = { ...state.processInstance, invoices };

    // Call updateCodes with the updated result
    const updatedState = updateCodes(state, {
      result: updatedResult
    });
    return {
      ...state,
      ...updatedState
    };
  });

  builder.addCase('UPDATE_TRANSACTION_OPTIMISTICALLY', (state, action: AnyAction) => {
    const payload = action.payload;
    const { addDTO, invoiceGuid } = payload;
    const invoices = cloneDeep(state.processInstance?.invoices || null);
    const invoiceIndex = invoices
      ? invoices.findIndex(inv => inv.guid === invoiceGuid)
      : -1;

    if (invoiceIndex !== -1) {
      const transactionIndex = invoices[invoiceIndex].transactions?.findIndex(
        txn => txn.guid === addDTO.transactionGuid
      );
      if (transactionIndex !== -1) {
        const transaction =
          invoices[invoiceIndex].transactions[transactionIndex];

        // Filter out 'transactionGuid' and assign all other properties
        const filteredAddDTO = Object.fromEntries(
          Object.entries(addDTO).filter(([key]) => key !== 'transactionGuid')
        );
        Object.assign(transaction, filteredAddDTO);
      }
    }
    const updatedResult = { ...state.processInstance, invoices };

    // Call updateCodes with the updated result
    const updatedState = updateCodes(state, {
      result: updatedResult
    });
    return {
      ...state,
      ...updatedState
    };
  });

  builder.addCase('CREATE_TRANSACTION_OPTIMISTICALLY', (state, action: AnyAction) => {
    const payload = action.payload;
    const { createDTO, invoiceGuid } = payload;

    const invoices = cloneDeep(state.processInstance?.invoices || []);
    const invoiceIndex = invoices.findIndex(inv => inv.guid === invoiceGuid);

    if (invoiceIndex !== -1) {
      const invoice = invoices[invoiceIndex];

      const existingTransactions = Array.isArray(invoice.transactions)
        ? invoice.transactions
        : [];

      const newTransaction = {
        procedure: createDTO.procedure,
        sequence_number: existingTransactions.length + 1,
        isTemporary: true
      };

      const updatedInvoice = {
        ...invoice,
        transactions: [...existingTransactions, newTransaction]
      };

      invoices[invoiceIndex] = updatedInvoice;
    }

    const updatedResult = { ...state.processInstance, invoices };

    const updatedState = updateCodes(state, {
      result: updatedResult
    });

    return {
      ...state,
      ...updatedState
    };
  });

  builder.addCase('REPLACE_OPTIMISTIC_TRANSACTION', (state, action: AnyAction) => {
    const payload = action.payload;
    const { transaction, invoiceGuid } = payload;

    const invoices = cloneDeep(state.processInstance?.invoices || []);
    const invoiceIndex = invoices.findIndex(inv => inv.guid === invoiceGuid);

    if (invoiceIndex !== -1) {
      const invoice = invoices[invoiceIndex];
      const existingTransactions = Array.isArray(invoice.transactions)
        ? invoice.transactions
        : [];

      // Find index of the temporary transaction
      const tempTxnIndex = existingTransactions.findIndex(
        txn => txn.isTemporary
      );

      // If found, replace it with the actual transaction
      if (tempTxnIndex !== -1) {
        const updatedTransactions = [...existingTransactions];
        updatedTransactions[tempTxnIndex] = transaction;

        const updatedInvoice = {
          ...invoice,
          transactions: updatedTransactions
        };

        invoices[invoiceIndex] = updatedInvoice;

        const updatedResult = { ...state.processInstance, invoices };
        const updatedState = updateCodes(state, {
          result: updatedResult
        });

        return {
          ...state,
          ...updatedState
        };
      }
    }

    return state;
  });

  builder.addCase(gradeBenchPanelActions.loadCQAItemFromURL.pending, state => {
    return {
      ...state,
      loadState: LOAD_STATES.started
    };
  });

  // There is a similar reducer for loadCQAItemFromURL action in grade-bench-panel.actions.ts
  builder.addCase(gradeBenchPanelActions.loadCQAItemFromURL.fulfilled, (state, action) => {
    const result = action.payload;

    return {
      ...state,
      // used for patient info right bar only
      // activeItem is initialized as {}
      // https://github.com/CodaMetrix/ui-omnibus/blob/44a150472de319e37fb210e9be6a593deea61959/packages/ui-common/src/core/amplify/state/code-bench.ts#L92
      activeItem: {
        ...convertKeysToSnakeCase(result.workItem),
        sd_case_id: result.sdCaseId,
        grader_updated_date: result.graderUpdatedDate,
        assigned_to_id: result.assignedToId,
      },
      loadState: LOAD_STATES.done,
      benchEditable: {
        ...state.benchEditable,
        editable: true,
        submitted: result.workItem.status == 'CQA_COMPLETE'
      }
    };
  });

  builder.addCase(gradeBenchPanelActions.getDocumentsAsHtmlFromCQAWorkItem.fulfilled, (state, action) => {
    const result = action.payload;
    return {
      ...state,
      notes: result,
      activeNote: result.length ? result[0] : undefined,
      documentMetadata: []
    };
  });

  builder.addCase('FETCH_CODES_WITH_ENCOUNTER/fulfilled', (state, action: AnyAction) => {
    const params = action.meta.arg as CMxCommonApp.EncounterCodesParams;
    const suggestedCodeResult = action.payload as CMxAPI.SuggestedCodeResponse;
    const codeDescriptions = { ...(state.codeDescriptions ?? {}) };
    const { diagnosis_codes, modifier_codes, procedure_codes } =
      suggestedCodeResult || {};
    const addCodeDescriptions = (codes = []) =>
      codes.forEach(
        code =>
          (codeDescriptions[code.code] =
            codeDescriptions[code.code] || (code as CMxCommonApp.CodeInfo))
      );

    const codeType = params?.codeType?.toUpperCase();

    const suggestedCodes = parseSuggestedCodes(
      suggestedCodeResult,
      state.suggestedCodes,
      codeType
    );

    [diagnosis_codes, modifier_codes, procedure_codes].forEach(
      addCodeDescriptions
    );

    return {
      ...state,
      suggestedCodes: {
        ...suggestedCodes
      },
      codeDescriptions
    };
  });

  builder.addCase('GET_RELATED_CODES/fulfilled', (state, action: AnyAction) => {
    const params = action.meta.arg as CMxCommonApp.RelatedCodesParams;
    const result = action.payload as any;
    const codeTypeKey = params?.codeType?.toLowerCase();

    // New endpoint returns a flat array; fall back to pageable .content for compatibility
    const codesArray = Array.isArray(result) ? result : (result?.content ?? []);

    const newRelatedCodes = codesArray
      .map((code: CMxCommonApp.CodeInfo) => ({
        ...code,
        suggested_code_type: SugggestedCodeType.RELATED_CASE,
        code_source: (code as any)?.code_source ?? '',
        // Preserve code_recent_date from the API response; sort below activates once backend populates it
        code_recent_date: (code as any)?.code_recent_date ?? '',
        transaction_guids: []
      }))
      .sort((a: any, b: any) => {
        const dateA = a.code_recent_date ? new Date(a.code_recent_date).getTime() : 0;
        const dateB = b.code_recent_date ? new Date(b.code_recent_date).getTime() : 0;
        return dateB - dateA;
      });

    const updatedSuggestedCodes: CMxCommonApp.SuggestedCodes = {
      ...state.suggestedCodes,
      [codeTypeKey]: {
        ...state.suggestedCodes?.[codeTypeKey],
        encounterList: newRelatedCodes
      }
    };

    // Build a new codeDescriptions object — do not mutate the draft reference,
    // as this reducer returns a new state value (mixing both causes Immer error #4)
    const newCodeDescriptions = { ...(state.codeDescriptions ?? {}) };
    newRelatedCodes.forEach(code => {
      newCodeDescriptions[code.code] = newCodeDescriptions[code.code] || code;
    });

    return {
      ...state,
      suggestedCodes: updatedSuggestedCodes,
      codeDescriptions: newCodeDescriptions
    };
  });

  builder.addCase('FETCH_CODE_BENCH_LAYOUT_CONFIGURATION/fulfilled', (state, action: AnyAction) => {
    const jsonValue = action.payload as any;
    if (jsonValue && Object.keys(jsonValue).length > 0) {
      // Convert structure to one the UI expects
      const layoutConfig: CMxCommonApp.PatientInformationConfiguration = {
        patient: Object.values(jsonValue.patient || {})
          .filter((value: any) => value !== null)
          .map((value: any) => {
            return {
              ...value
            };
          }),
        summary: Object.values(jsonValue.summary || {})
          .filter((value: any) => value !== null)
          .map((value: any) => {
            return {
              ...value
            };
          }),
        expanded: Object.values(jsonValue.expanded || {})
          .filter((value: any) => value !== null)
          .map((value: any) => {
            return {
              ...value
            };
          })
      };

      return {
        ...state,
        dynamicPropertyConfig: layoutConfig,
        propertyConfig: layoutConfig
      };
    }
    return state;
  });

  builder.addCase('SET_CODE_BENCH_PROPERTY_CONFIG', (state, action: AnyAction) => {
    const propertyConfig = action.payload;
    return {
      ...state,
      propertyConfig
    };
  });

  builder.addCase('FETCH_RELATED_CASE_FILTER_DICTIONARIES/fulfilled', (state, action: AnyAction) => {
    const filterData = action.payload as { [key: string]: CMxCommonApp.NoteTypeRow[] | CMxCommonApp.NoteSpecialtyRow[] | CMxCommonApp.PatientClassRow[] };
    const { activeItem } = state;
    const noteTypeOptions = convertToFilterRecord(
      'Note Type',
      'Note Type',
      'Note Description',
      'Note Description',
      filterData?.['noteType'],
      false,
      2
    );

    noteTypeOptions.filterOptions.forEach(option => {
      const item = (filterData?.[
        'noteType'
      ] as CMxCommonApp.NoteTypeRow[])?.find(
        item =>
          !isEmpty(item?.['Note Description']) &&
          item?.['Note Description'] === option.key &&
          (item?.['Filter Default'] ===
            commonEnums.DictionaryFilterDefault.INCLUDE ||
            !item.hasOwnProperty('Filter Default'))
      );
      option.defaultSelection =
        !!item || option.key === 'noneKey' ? 'INCLUDE' : 'EXCLUDE';
    });

    const patientClassOptions = convertToFilterRecord(
      'Patient Class',
      'Patient Class',
      'Patient Class',
      'Patient Class',
      filterData?.['patientClass'],
      true,
      3
    );

    const noteSpecialtyOptions = convertToFilterRecord(
      'Specialty',
      'Specialty',
      'Note Specialty',
      'Note Specialty',
      filterData?.['noteSpecialty'],
      true,
      4
    );
    const dateOfService: Date = new Date(
      cmxDateTime.format(
        activeItem.date_of_service,
        cmxDateTime.FORMATS.DATE_OF_SERVICE
      )
    );
    let startDate: Date;
    let endDate: Date;
    if (activeItem?.admission_datetime && activeItem?.discharge_datetime) {
      startDate = new Date(
        cmxDateTime.format(
          activeItem.admission_datetime,
          cmxDateTime.FORMATS.DATE_OF_SERVICE
        )
      );
      endDate = new Date(
        cmxDateTime.format(
          activeItem.discharge_datetime,
          cmxDateTime.FORMATS.DATE_OF_SERVICE
        )
      );
    } else {
      endDate = new Date();
      let pastDate = cmxDateTime.format(endDate, cmxDateTime.FORMATS.DATE, {
        throw: false,
        localized: true,
        offset: {
          action: cmxDateTime.DateManipulateOperation.SUBTRACT,
          delta: [30, cmxDateTime.DateManipulateUnit.DAY]
        }
      });
      startDate = new Date(pastDate);
    }
    const dateFilter = {
      viewType: 'date_range_with_preset',
      type: 'date_range',
      label: 'Date range',
      displayOrder: 1,
      dateFilterOptions: {
        startDate: startDate,
        endDate: endDate,
        datePresets: [
          {
            key: 'date_of_service',
            name: 'Date of Service',
            displayOrder: 1,
            startDate: dateOfService,
            endDate: dateOfService
          },
          {
            key: 'past_7_days',
            name: 'Past 7 days',
            displayOrder: 2,
            duration: 7,
            durationType: 'days'
          },
          {
            key: 'past_30_days',
            name: 'Past 30 days',
            displayOrder: 3,
            duration: 30,
            durationType: 'days'
          },
          {
            key: 'past_3_years',
            name: 'Past 3 years',
            displayOrder: 4,
            duration: 3,
            durationType: 'years'
          }
        ]
      }
    };
    return {
      ...state,
      filterData: [
        noteTypeOptions,
        patientClassOptions,
        noteSpecialtyOptions,
        dateFilter
      ]
    };
  });

  builder.addCase('UPDATE_RELATED_CASE_FILTER_SELECTION', (state, action: AnyAction) => {
    const relatedCasesFilterSelection = action.payload;
    return {
      ...state,
      relatedCasesFilterSelection
    };
  });

  builder.addCase('REFRESH_HOLD_SUBMISSION', state => {
    const updatedState = updateCodes(state, {
      result: { ...state.processInstance }
    });
    return {
      ...state,
      ...updatedState
    };
  });
});

export { codeBench };
